import type React from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import Modal from '@common/components/ConstellationV10Modal';
import userPersonas from '@common/constants/userPersonas';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { isDueDateValid } from './validation';
import dueDates from './dueDates.json';
import {
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Label,
  Paragraph,
  Select,
  TextButton,
} from '@zillow/constellation-10';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { RootState } from '../../../../../store/types';

interface Props {
  paymentDayOfTheMonth: number;
  editMode: boolean;
  onSubmit: (requestPayload: Record<string, unknown>) => any;
  onClose: () => void;
  onCancel: () => void;
  onBack: (requestPayload: Record<string, unknown>) => void;
  trackValidationError: (data: { errors: Array<Record<string, any>> }) => void;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}

const DueDatePage: React.FC<Props> = ({
  paymentDayOfTheMonth: initialPaymentDayOfTheMonth = 1,
  editMode = false,
  onSubmit,
  onClose,
  onCancel,
  onBack,
  trackValidationError,
  currentProperty,
  user,
}) => {
  const { enableFastPayments } = useSelector((state: RootState) => state.payments.landlord.property.userConfig);
  const [paymentDayOfTheMonth, setPaymentDayOfTheMonth] = useState(initialPaymentDayOfTheMonth);
  const [showCaveat, setShowCaveat] = useState(false);
  const [error, setError] = useState('');
  const [editPaymentDayOfTheMonth, setEditPaymentDayOfTheMonth] = useState(false);

  const onChangeSelect = (e): void => {
    const selectedOption = e.currentTarget.options[e.currentTarget.selectedIndex];
    const newPaymentDayOfTheMonth = parseInt(selectedOption.getAttribute('value'), 10);
    const newShowCaveat = selectedOption.innerText?.endsWith('*');

    setShowCaveat(newShowCaveat);
    setPaymentDayOfTheMonth(newPaymentDayOfTheMonth);
    setError('');
    setEditPaymentDayOfTheMonth(true);
  };

  const handleSubmit = (): Promise<any> => {
    return Promise.resolve().then(() => {
      const validationError = isDueDateValid(paymentDayOfTheMonth);

      if (validationError) {
        setError(validationError);
        trackValidationError({ errors: [{ dueDateError: validationError }] });
        return { error: true };
      }

      IreneOnboardingCSEvents.emitContinueFromDueDatePageEvent({ currentProperty, user });

      const payload = editMode
        ? {
            editDayOfTheMonth: paymentDayOfTheMonth,
          }
        : {
            paymentDayOfTheMonth,
          };

      return onSubmit(payload);
    });
  };

  const handleCancel = (): void => {
    onCancel();
  };

  const handleClose = (): void => {
    IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'due_day',
    });
    onClose();
  };

  const gotoPreviousPage = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'due_day',
    });
    onBack({
      paymentDayOfTheMonth,
      editPaymentDayOfTheMonth,
    });
  };

  const renderStartDateOptions = () => {
    const defaultOption = (
      <option key={-1} disabled value="">
        Select one
      </option>
    );
    const options = dueDates.map((date, index) => {
      if (date === null) {
        return (
          <option key={index} disabled>
            ──────
          </option>
        );
      }

      const [text, value] = date;

      return (
        <option key={index} value={value}>
          {text}
        </option>
      );
    });

    return [defaultOption, ...options];
  };

  const startDateOptions = renderStartDateOptions();

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={handleClose}
      header="Payment setup"
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <Paragraph textStyle="body-lg-bold">When should your tenant send their rent each month?</Paragraph>
          <FormField
            control={
              <Select size="md" value={paymentDayOfTheMonth} onChange={onChangeSelect}>
                {startDateOptions}
              </Select>
            }
            label={<Label>Monthly payment date</Label>}
            {...(error && { help: <FormHelp error>{error}</FormHelp> })}
          />

          {showCaveat && (
            <Paragraph textStyle="body-sm">
              For any month with fewer than {paymentDayOfTheMonth} days, the last day of the month will be the default
              payment date.
            </Paragraph>
          )}

          <Paragraph textStyle="body-sm">
            We&apos;ll ask your renter to send their rent&nbsp;
            <strong>on or before this day.</strong>
          </Paragraph>
          <Paragraph textStyle="body-sm">
            {enableFastPayments ? (
              <>
                Payments are expected to be deposited within two business days. In some cases, such as when a renter is
                within their first week of setting up and starting payments on Zillow, payments follow a standard
                five-business-day timeline. Learn more about the differences in payment timing{' '}
                <a
                  href="https://www.zillow.help/article/What-are-Fast-Payments"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </>
            ) : (
              <>
                Once they send payment, it will be deposited up to <strong>5 business days</strong> later, depending on
                the banks’ processing times.
              </>
            )}
          </Paragraph>
          <FaqComponent
            faqs={
              enableFastPayments
                ? FaqConstants.ONBOARDING_V2.DUE_DATE_PAGE_FAST_PAYMENTS
                : FaqConstants.ONBOARDING_V2.DUE_DATE_PAGE
            }
          />
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Payment due date actions">
          <TextButton onClick={gotoPreviousPage}>Back</TextButton>
          <Button emphasis="filled" onClick={handleSubmit}>
            {editMode ? 'Update' : 'Continue'}
          </Button>
          {editMode && (
            <Button emphasis="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </ButtonGroup>
      }
    />
  );
};

export default DueDatePage;
